import React, { FC } from 'react'
import KeepTruckinLogo from 'assets/logos/keeptruckin-logo.svg'
import Project44Logo from 'assets/logos/project44-small-logo.png'
import TransfixLogo from 'assets/logos/transfix-logo.svg'
import copy from 'copy-to-clipboard'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { ITrackingStateEnum } from 'types/graphqlTypes'
import { IShipperTrackingSegment, ITrackingStatus } from 'types/shipment'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { trackingRequirementFullDescription } from 'apps/booking/utils/trackingRequirements'
import { TrackingOverview } from 'apps/marketplace/apiTypes/shipments'
import { ConnectedPhoneNumber } from 'apps/marketplace/components/ConnectedPhoneNumber'
import {
  TrackingIcon,
  InfoIcon,
  CheckFilledIcon,
  PhoneIcon,
  EmailIcon,
  CopyIcon,
  SmartphoneIcon,
} from 'transfix-icons'
import { brandColors, Button, calcRem, Tooltip } from 'transfix-ui'
import { formatDriverLastPing } from 'utils/formatDatetime'
import { DISPLAY } from 'constants/display'
import { DetailItem, MultipleSkeletons } from './DetailItem'
import { TrackingTooltip } from './TrackingTooltip'
import { useCopied } from './useCopied'
import { getTrackingMethodName, getValidatedELD } from './utils'

export interface ITrackingDetailsProps {
  trackingOverview: TrackingOverview | null
  shipperTrackingSegment?: Pick<IShipperTrackingSegment, 'id' | 'description' | 'level'>
  trackingStatus?: ITrackingStatus
  shipmentId: number
  isLoading: boolean
}

interface ITrackingConfigData {
  backgroundColor: string
  textColor: string
}

const useTrackingDetailsStyles = makeStyles({
  pingText: {
    color: brandColors.coolGray6,
  },
  copyButton: {
    cursor: 'pointer',
  },
  wrapContent: {
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: calcRem(14),
  },
})

export const TRACKING_STATE_CONFIG: Record<ITrackingStateEnum, ITrackingConfigData> = {
  currently_tracking: {
    backgroundColor: brandColors.infoLight,
    textColor: brandColors.info,
  },
  was_tracking: {
    backgroundColor: brandColors.warningLight,
    textColor: brandColors.warning,
  },
  not_tracking_required: {
    backgroundColor: brandColors.errorLight,
    textColor: brandColors.error,
  },
  not_tracking_not_required: {
    backgroundColor: brandColors.coolGray1,
    textColor: brandColors.coolGray6,
  },
}

interface ICalculatedTrackingMethod {
  value?: string
  lastPingAt?: string | null
  isDriverTracking?: boolean
  driverLocationTimezone?: string | null
}

const getLastPing = (calculatedTrackingMethod: ICalculatedTrackingMethod) => {
  const { driverLocationTimezone, isDriverTracking, lastPingAt, value } = calculatedTrackingMethod

  if (!isDriverTracking && lastPingAt === null) {
    return value ? 'No pings yet' : DISPLAY.empty
  }

  if (!isDriverTracking) {
    const formattedDriverLastPing = formatDriverLastPing(driverLocationTimezone, lastPingAt)
    return `Last ping ${formattedDriverLastPing}`
  }

  const formattedDriverLastPing = formatDriverLastPing(driverLocationTimezone, lastPingAt)

  return `Last ping ${formattedDriverLastPing}`
}

export const TrackingDetails: FC<ITrackingDetailsProps> = ({
  isLoading,
  shipmentId,
  shipperTrackingSegment,
  trackingOverview,
  trackingStatus,
}) => {
  const classes = useTrackingDetailsStyles()
  const {
    calculatedTrackingMethod,
    carrier,
    dispatcher,
    driver,
    selectedTrackingMethod,
    trackingCapabilities,
    truckNumber: truck,
  } = trackingOverview || {}
  const { value: lastPingTrackingMethod = '' } = calculatedTrackingMethod || {}
  const { keepTruckin = false, mobile = false, project44 = false } = trackingCapabilities || {}
  const {
    email: dispatcherEmail = '',
    name: dispatcherName = '',
    parentUserId: dispatcherId = '',
    phone: dispatcherPhone = '',
  } = dispatcher || {}
  const {
    driverLocationTimezone,
    isDriverTracking,
    lastPingAt: driverLastPingAt,
    name: driverName = '',
    parentUserId: driverId = '',
    phone: driverPhone = '',
  } = driver || {}
  const { id: carrierId } = carrier || {}
  const { keepTruckinValidated = false, project44Validated = false, value: truckNumber } =
    truck || {}

  const { trackingState } = trackingStatus || {}
  const { description, level } = shipperTrackingSegment || {}

  const [isCopied, setIsCopied] = useCopied()
  const copyTextHandler = () => {
    copy(dispatcherEmail)

    setIsCopied(true)
  }
  const goToDriverPage = () => {
    const win = window.open(`${__ROOT__}/users/${driverId}`, '_blank')
    if (win) win.focus()
  }
  const goToCarrierPage = () => {
    const win = window.open(`${__ROOT__}/carriers/${carrierId}`, '_blank')
    if (win) win.focus()
  }

  const trackingMethod = selectedTrackingMethod
    ? (selectedTrackingMethod as TRACKING_METHOD_TYPE)
    : undefined
  const lastPing = calculatedTrackingMethod ? getLastPing(calculatedTrackingMethod) : ''
  const driverLastPing = getLastPing({
    value: selectedTrackingMethod || undefined,
    driverLocationTimezone,
    isDriverTracking,
    lastPingAt: driverLastPingAt,
  })
  const lastPingTrackingMethodName = getTrackingMethodName(lastPingTrackingMethod)
  const { backgroundColor, textColor } = trackingState
    ? TRACKING_STATE_CONFIG[trackingState]
    : TRACKING_STATE_CONFIG.not_tracking_not_required
  const trackingLevelMessage = level ? trackingRequirementFullDescription.get(level) : ''

  const trackingMethodName = getTrackingMethodName(trackingMethod)
  const validatedELD = getValidatedELD(trackingMethod, keepTruckinValidated, project44Validated)
  const isValidELD = Boolean(validatedELD.length)

  return (
    <Box width={343}>
      <Typography variant='h3'>Tracking Details {shipmentId ? `- #${shipmentId}` : ''}</Typography>
      <Box mt={3} bgcolor={backgroundColor} padding={1.5}>
        <Box display='flex' color={textColor}>
          <TrackingIcon />
          <Box ml={1.5}>
            {isLoading ? (
              <Skeleton width={120} />
            ) : (
              <Typography variant='subtitle1' style={{ color: textColor }}>
                {lastPingTrackingMethodName
                  ? `Tracking via ${lastPingTrackingMethodName}`
                  : 'Not Tracking'}
              </Typography>
            )}
            {isLoading ? (
              <Skeleton width={80} />
            ) : (
              <Typography variant='caption' className={classes.pingText}>
                {lastPing || DISPLAY.empty}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box mt={2.5}>
        <DetailItem
          isLoading={isLoading}
          title='Requirement'
          value={
            <Box display='flex'>
              <Typography variant='caption'>
                Level {level} - {description}
              </Typography>
              <Tooltip
                position='top'
                id={`trackingLevel-${shipmentId}`}
                trigger={
                  <Box ml={0.625} color={brandColors.coolGray5}>
                    <InfoIcon />
                  </Box>
                }>
                <Box>
                  Level {level}: {description}
                </Box>
                <Box>{trackingLevelMessage}</Box>
              </Tooltip>
            </Box>
          }
          isEmpty={!level}
          emptyState={DISPLAY.empty}
        />
        <DetailItem
          isLoading={isLoading}
          title='Selected'
          value={<Typography variant='caption'>{trackingMethodName}</Typography>}
          isEmpty={!trackingMethodName}
          emptyState={DISPLAY.empty}
        />
        <DetailItem
          isLoading={isLoading}
          title='Truck #'
          value={
            <Box display='flex'>
              <Typography variant='caption'>{truckNumber}</Typography>
              {isValidELD && (
                <TrackingTooltip
                  isValid={isValidELD}
                  shipmentId={shipmentId}
                  validatedELD={validatedELD}
                />
              )}
            </Box>
          }
          isEmpty={!truckNumber}
          emptyState={
            <>
              <Box mb={1}>{DISPLAY.empty}</Box>
              <InlineMessage
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                message='Valid Truck # Required'
              />{' '}
            </>
          }
        />
        <DetailItem
          isLoading={isLoading}
          loadingNode={<MultipleSkeletons count={3} />}
          title='Dispatcher'
          value={
            <Box>
              <Box>
                {/* TODO: Fix me when fix react router dom issue was done */}
                <a
                  className={classes.wrapContent}
                  href={`${__ROOT__}/users/${dispatcherId}`}
                  target='_blank'
                  rel='noopener noreferrer'>
                  {dispatcherName || DISPLAY.empty}
                </a>
              </Box>
              <Box display='flex'>
                <Box mr={0.625} color={brandColors.coolGray5}>
                  <PhoneIcon />
                </Box>
                {dispatcherPhone ? (
                  <ConnectedPhoneNumber phoneNumber={dispatcherPhone} />
                ) : (
                  DISPLAY.empty
                )}
              </Box>
              <Box mt={1} display='flex'>
                <Box mr={0.625} color={brandColors.coolGray5}>
                  <EmailIcon />
                </Box>
                <Typography className={classes.wrapContent} variant='caption'>
                  {dispatcherEmail || DISPLAY.empty}
                </Typography>
                {dispatcherEmail && (
                  <Box ml={0.625} color={brandColors.skyBlue6} onClick={copyTextHandler}>
                    {isCopied ? <CheckFilledIcon /> : <CopyIcon className={classes.copyButton} />}
                  </Box>
                )}
              </Box>
            </Box>
          }
          isEmpty={!dispatcherId}
          emptyState={DISPLAY.empty}
        />
        <DetailItem
          isLoading={isLoading}
          title='Driver'
          loadingNode={<MultipleSkeletons count={2} />}
          value={
            <Box>
              <Box>
                {/* TODO: Fix me when fix react router dom issue was done */}
                <a
                  href={`${__ROOT__}/users/${driverId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={classes.wrapContent}>
                  {driverName}
                </a>
              </Box>
              <Box display='flex'>
                <Box mr={0.625} color={brandColors.coolGray5}>
                  <SmartphoneIcon />
                </Box>
                <ConnectedPhoneNumber phoneNumber={driverPhone} />
              </Box>
            </Box>
          }
          isEmpty={!driverId}
          emptyState={
            <>
              <Box mb={1}>{DISPLAY.empty}</Box>
              <InlineMessage
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                message='Driver Phone # Required'
              />{' '}
            </>
          }
        />
        <DetailItem
          isLoading={isLoading}
          title='Driver Last Ping'
          value={<Typography variant='caption'>{driverLastPing}</Typography>}
          isEmpty={!driverLastPing}
          emptyState={DISPLAY.empty}
        />
        <DetailItem
          isLoading={isLoading}
          title='Capabilities'
          emptyState={DISPLAY.empty}
          isEmpty={!(keepTruckin || project44 || mobile)}
          value={
            <Box display='flex'>
              {keepTruckin && (
                <Tooltip
                  id={`keeptruckin-${shipmentId}`}
                  trigger={
                    <Box mr={1} display='flex'>
                      <img width='26px' src={KeepTruckinLogo} alt='KeepTruckin' />
                    </Box>
                  }>
                  This carrier has tracked using KeepTruckin
                </Tooltip>
              )}
              {project44 && (
                <Tooltip
                  id={`project44-${shipmentId}`}
                  trigger={
                    <Box mr={1} display='flex'>
                      <img width='26px' src={Project44Logo} alt='project44' />
                    </Box>
                  }>
                  This carrier has tracked using project44
                </Tooltip>
              )}
              {mobile && (
                <Tooltip
                  id={`mobile-${shipmentId}`}
                  trigger={
                    <Box display='flex'>
                      <img width='26px' src={TransfixLogo} alt='Transfix' />
                    </Box>
                  }>
                  This carrier has tracked via the Transfix App
                </Tooltip>
              )}
            </Box>
          }
        />
        <Box mt={3}>
          <Button
            color='secondary'
            fullWidth
            onClick={goToDriverPage}
            disabled={!driverId || isLoading}>
            View Driver App Status
          </Button>
        </Box>
        <Box mt={1}>
          <Button
            color='primary'
            fullWidth
            onClick={goToCarrierPage}
            disabled={isLoading || !carrierId}>
            View Carrier Profile
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
